import type React from "react";
import { useEffect } from "react";
import { FormattedHTMLMessage } from "react-intl";

import { logger } from "@jslib/shared/utils/logger";

import { type VerificationService, type ViewModel } from "../../lib/types/types";
import { submitForm } from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { type CollectInfoSteps, VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { isFormFilled } from "../../lib/validators/validators";
import { setRef } from "../../lib/refs/refs";
import {
  type CollectStepStartEvent,
  type CollectStepSubmitEvent,
  CustomMetricNames,
  EventName,
  recordGaEvent,
} from "../../lib/GoogleAnalytics/ga";
import {
  COLLECT_INFO_SUBMISSION_TIME,
  COLLECT_INFO_SUBMIT,
  useMetrics,
} from "../FeatureFlags/useMetrics";

interface PersonalInfoStepSubmitButtonProps {
  step: CollectInfoSteps;
  verificationService: VerificationService;
  viewModel: ViewModel;
  buttonText: React.ReactNode;
  failedInstantMatch?: boolean;
}

const wrapperClassNames: Record<CollectInfoSteps, string> = {
  collectStudentPersonalInfo: "sid-student--submit",
  collectSeniorPersonalInfo: "sid-senior-submit",
  collectTeacherPersonalInfo: "sid-teacher-submit",
  collectMoverPersonalInfo: "sid-mover-submit",
  collectInactiveMilitaryPersonalInfo: "sid-inactive-military-submit sid-military-submit",
  collectActiveMilitaryPersonalInfo: "sid-active-military-personal-submit sid-military-submit",
  collectMilitaryPersonalInfo: "sid-active-military-personal-submit sid-military-submit",
  collectMilitaryStatus: "",
  collectMemberPersonalInfo: "sid-member-submit",
  collectMedicalProfessionalPersonalInfo: "sid-medical-submit",
  collectLowIncomePersonalInfo: "sid-low-income-submit",
  collectLicensedProfessionalPersonalInfo: "sid-licensed-professional-submit",
  collectHybridIdentityPersonalInfo: "sid-hybrid-identity-submit",
  collectGeneralIdentityPersonalInfo: "sid-general-identity-submit",
  collectDriverLicensePersonalInfo: "sid-drivers-license-submit",
  collectFirstResponderPersonalInfo: "sid-first-responder-personal-info-submit",
  collectEmployeePersonalInfo: "sid-employment-submit",
  collectAgePersonalInfo: "sid-age-personal-info-submit",
  collectIdentifier: "sid-identifier-submit",
  collectPersonalInfo: "sid-personal-info-submit",
};

const buttonClassNames: Record<CollectInfoSteps, string> = {
  collectStudentPersonalInfo: "sid-student-submit-btn",
  collectSeniorPersonalInfo: "sid-senior-submit-btn",
  collectTeacherPersonalInfo: "sid-teacher-submit-btn",
  collectMoverPersonalInfo: "sid-mover-submit-btn",
  collectInactiveMilitaryPersonalInfo: "sid-inactive-military-submit-btn sid-military-submit-btn",
  collectActiveMilitaryPersonalInfo:
    "sid-active-military-personal-submit-btn sid-military-submit-btn",
  collectMilitaryPersonalInfo: "sid-active-military-personal-submit-btn sid-military-submit-btn",
  collectMilitaryStatus: "",
  collectMemberPersonalInfo: "sid-member-submit-btn",
  collectMedicalProfessionalPersonalInfo: "sid-medical-submit-btn",
  collectLowIncomePersonalInfo: "sid-low-income-submit-btn",
  collectLicensedProfessionalPersonalInfo: "sid-licensed-professional-submit-btn",
  collectHybridIdentityPersonalInfo: "sid-hybrid-identity-submit-btn",
  collectGeneralIdentityPersonalInfo: "sid-general-identity-submit-btn",
  collectDriverLicensePersonalInfo: "sid-drivers-license-submit-btn",
  collectFirstResponderPersonalInfo: "sid-first-responder-submit-btn",
  collectEmployeePersonalInfo: "sid-employment-submit-btn",
  collectAgePersonalInfo: "sid-age-personal-info-btn",
  collectIdentifier: "sid-identifier-submit-btn",
  collectPersonalInfo: "sid-personal-info-submit-btn",
};

const usesWrapperID: CollectInfoSteps[] = [
  VerificationStepsEnum.collectMoverPersonalInfo,
  VerificationStepsEnum.collectStudentPersonalInfo,
  VerificationStepsEnum.collectEmployeePersonalInfo,
];

/**
 * A shared wrapper for all personal info collect steps.
 */
export const PersonalInfoStepSubmitButton: React.FC<PersonalInfoStepSubmitButtonProps> = ({
  step,
  verificationService,
  viewModel,
  buttonText,
  failedInstantMatch = false,
}) => {
  const { userDidConvert, getTimeOnForm } = useMetrics({
    segment: verificationService.verificationResponse.segment,
    programId: verificationService.programId,
  });

  useEffect(() => {
    const event: CollectStepStartEvent = {
      eventName: EventName.COLLECT_STARTED,
      params: {},
    };

    recordGaEvent(event);
  }, []);

  const successCallback = () => {
    try {
      const event: CollectStepSubmitEvent = {
        eventName: EventName.COLLECT_SUBMITTED,
        params: {
          [CustomMetricNames.EVENT_DURATION]: getTimeOnForm().duration,
        },
      };
      recordGaEvent(event);
    } catch (e) {
      logger.warn("Unable to measure time on form");
    }

    userDidConvert(COLLECT_INFO_SUBMIT, COLLECT_INFO_SUBMISSION_TIME);
  };

  // The majority of the class/id dancing in this component are to support the
  // existing inconsistencies in our different collect info steps when they were
  // brought together into this shared component. When we get away from css based
  // customization this can get much saner
  const wrapperCls = wrapperClassNames[step];
  const buttonCls = buttonClassNames[step];

  const btnId = usesWrapperID.includes(step)
    ? "sid-submit-wrapper__collect-info"
    : "sid-submit-btn-collect-info";

  const btnText = failedInstantMatch ? (
    <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
  ) : (
    buttonText
  );

  const textSpanProps =
    step === VerificationStepsEnum.collectEmployeePersonalInfo
      ? { className: "sid-employment-submit-btn__text" }
      : {};
  const isDisabled = !isFormFilled(viewModel, verificationService.formValidationOptions);

  return (
    <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
      <div className={`${wrapperCls} sid-submit`}>
        <button
          id={btnId}
          onClick={() => submitForm(viewModel, verificationService, step, successCallback)}
          type="submit"
          className={`sid-btn ${buttonCls} sid-btn--dark sid-l-full-width ${
            isDisabled ? "sid-btn--disabled-like" : ""
          }`}
          aria-labelledby="verify-status-text"
          ref={(button) => setRef("submitButton", button)}
          aria-disabled={isDisabled}
          data-testid="submit-button"
        >
          <span id="verify-status-text" {...textSpanProps}>
            {btnText}
          </span>
        </button>
      </div>
    </div>
  );
};
